import React, { useState, forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { StyledModal, StyledModalHandle, Text, View } from '@constants/Themed';
import Button from '@components/UI/Button/Button';
import UserFinder from '@components/Global/UserFinder/UserFinder';
import apis from '@api/api';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { KeyboardAvoidingView, FlatList, Platform, TouchableOpacity } from 'react-native';
import UserListItem from './UserListItem';
import { useDeviceType } from '@hooks/useDeviceType';
import * as Animatable from 'react-native-animatable';
import { Picker } from '@react-native-picker/picker';

type AddUserPropTypes = {
  departmentItems: Array<any>;
  onModalClose?: () => void;
  company?: any
}

export type AddUserModalHandle = {
  openModal: () => void;
  closeModal: () => void;
}

const AddUserModal = forwardRef<AddUserModalHandle, AddUserPropTypes>((props: AddUserPropTypes, ref) => {
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const { isTablet } = useDeviceType();
  const modalRef = useRef<StyledModalHandle>(null);

  useImperativeHandle(ref, () => ({
    openModal: () => modalRef.current?.openModal?.(),
    closeModal: () => modalRef.current?.closeModal?.()
  }));

  const handleUserSelection = useCallback((users: any) => {
    console.log('Users selected from UserFinder:', users); // Debug log

    // Handle different scenarios from UserFinder
    if (!users) {
      setSelectedUsers([]);
      return;
    }

    // If users is an array, use it directly and add default role
    if (Array.isArray(users)) {
      const usersWithRoles = users.map(user => ({ ...user, role: user.role || 'member' }));
      setSelectedUsers(usersWithRoles);
      return;
    }

    // If users is a single user object, wrap in array and add default role
    if (typeof users === 'object' && users.value) {
      // Check if user is already selected to avoid duplicates
      setSelectedUsers(prev => {
        const existingUser = prev.find(u => u.value === users.value);
        if (existingUser) {
          return prev; // Don't add duplicate
        }
        return [...prev, { ...users, role: users.role || 'member' }];
      });
      return;
    }

    // Fallback
    setSelectedUsers([]);
  }, []);

  const removeUser = useCallback((userId: string) => {
    setSelectedUsers(prev => prev.filter(user => user.id !== userId && user.value !== userId));
  }, []);

  const updateUserRole = useCallback((userId: string, newRole: string) => {
    setSelectedUsers(prev =>
      prev.map(user =>
        (user.id === userId || user.value === userId)
          ? { ...user, role: newRole }
          : user
      )
    );
  }, []);

  const handleInviteUsers = async () => {
    if (selectedUsers.length === 0) return;

    setIsLoading(true);
    try {
      // Prepare users data with roles
      const usersWithRoles = selectedUsers.map(user => ({
        ...user,
        role: user.role || 'member' // Ensure every user has a role
      }));

      const result = await apis.addUserToCompany({ users: usersWithRoles });
      console.log('Users added with roles:', result);
      modalRef.current?.closeModal?.();
      props.onModalClose?.();
    } catch (error) {
      console.error('Error adding users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetModal = () => {
    setSelectedUsers([]);
    setIsLoading(false);
  };

  // Available roles for users
  const userRoles = [
    { label: 'Member', value: 'member' },
    { label: 'Admin', value: 'admin' },
    { label: 'Manager', value: 'manager' },
    { label: 'Viewer', value: 'viewer' }
  ];

  // Custom render item for selected users with role selection
  const renderSelectedUserItem = ({ item, index }: { item: any, index: number }) => (
    <Animatable.View
      animation="fadeInRight"
      delay={index * 100}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: 12,
        padding: 15,
        marginVertical: 4,
        borderLeftWidth: 3,
        borderLeftColor: '#007AFF'
      }}
    >
      <View style={{ flex: 1 }}>
        <Text style={{ fontWeight: '600', fontSize: 16, marginBottom: 4 }}>
          {item.label || item.name || item.value}
        </Text>
        <Text style={{ fontSize: 14, opacity: 0.7 }}>
          {item.email || item.value}
        </Text>
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <View style={{
          backgroundColor: 'rgba(0, 122, 255, 0.1)',
          borderRadius: 8,
          paddingHorizontal: 8,
          paddingVertical: 4,
          minWidth: 90,
          borderWidth: 1,
          borderColor: 'rgba(0, 122, 255, 0.3)'
        }}>
          <Picker
            selectedValue={item.role || 'member'}
            onValueChange={(value) => updateUserRole(item.id || item.value, value)}
            style={{
              height: Platform.OS === 'ios' ? 40 : 35,
              fontSize: 13
            }}
            itemStyle={Platform.OS === 'ios' ? {
              fontSize: 13,
              height: 40
            } : undefined}
          >
            {userRoles.map(role => (
              <Picker.Item key={role.value} label={role.label} value={role.value} />
            ))}
          </Picker>
        </View>

        <TouchableOpacity
          onPress={() => removeUser(item.id || item.value)}
          style={{
            backgroundColor: 'rgba(255, 59, 48, 0.1)',
            borderRadius: 8,
            padding: 8,
            borderWidth: 1,
            borderColor: 'rgba(255, 59, 48, 0.3)'
          }}
        >
          <Text style={{ color: '#FF3B30', fontWeight: '600', fontSize: 14 }}>×</Text>
        </TouchableOpacity>
      </View>
    </Animatable.View>
  );

  return (
    <StyledModal
      ref={modalRef}
      title="Add Users to Organization"
      caption="Search for users by email or name to invite them to your organization. If they don't have an account, we'll send them an invitation."
      onModalWillHide={resetModal}
      extraChildren={(
        < View style={{
          paddingVertical: 15,
          alignItems: 'center'
        }}>
          <Button
            disabled={selectedUsers.length === 0 || isLoading}
            onPress={handleInviteUsers}
            icon={isLoading ? undefined : "plus"}
            buttonText={isLoading ? 'Sending Invites...' : `Invite ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`}
            textColor='white'
            iconColor='white'
            customStyle={{
              shadowOpacity: 0,
              borderRadius: 12,
              paddingHorizontal: 25,
              minHeight: 50,
              minWidth: 180,
              opacity: selectedUsers.length === 0 || isLoading ? 0.6 : 1
            }}
          />
        </View>
    )}
    >
      <View style={{
        flex: 1,
        paddingHorizontal: Platform.select({ web: 25, default: 20 }),
        paddingVertical: 15
      }}>

        <KeyboardAvoidingView
          behavior='padding'
          keyboardVerticalOffset={100}
          style={{ flex: 1, zIndex: 100, maxWidth: '100%' }}
        >
          {/* User Search Section */}
          <View style={{ marginBottom: 20 }}>
            <Text style={{
              fontSize: 16,
              fontWeight: '600',
              marginBottom: 12,
            }}>
              Search for Users:
            </Text>
            <UserFinder
              forceRow={false}
              hideList
              setCallback={handleUserSelection}
              onOpen={() => console.log('Dropdown opened')}
              onClose={() => console.log('Dropdown closed')}
            />
          </View>
        </KeyboardAvoidingView>

        {/* Selected Users Display */}
        {selectedUsers.length > 0 ? (
          <Animatable.View
            animation="fadeInUp"
            duration={300}
            style={{ flex: 2 }}
          >
            <Text style={{
              fontSize: 16,
              fontWeight: '600',
              marginBottom: 10,
            }}>
              Selected Users ({selectedUsers.length}):
            </Text>

            <FlatList
              style={{
                flex: 1,
                backgroundColor: 'rgba(0, 122, 255, 0.05)',
                borderRadius: 12,
                paddingVertical: 10,
                marginBottom: 20,
                maxHeight: isTablet ? 300 : 250
              }}
              contentContainerStyle={{
                paddingHorizontal: 15,
                gap: 8
              }}
              data={selectedUsers}
              showsVerticalScrollIndicator={true}
              renderItem={renderSelectedUserItem}
              keyExtractor={(item, index) => `${item.id || item.value || index}`}
            />
          </Animatable.View>
        ) : (
          <View style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            paddingVertical: 50
          }}>
            <Text style={{
              fontSize: 16,
              opacity: 0.6,
              textAlign: 'center',
              lineHeight: 24
            }}>
              Search above to find users{'\n'}and invite them to your organization
            </Text>
          </View>
        )}


      </View>
    </StyledModal>
  );
});

export default AddUserModal;